/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import useSessionStore from 'stores/useSessionStore';
import { getRptOpinions, saveRptOpinions } from 'apis/ut/UtMatrixComparisonReportList';
import { GlobalBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { RptOpinion } from 'models/ut/UtMatrixComparisonReportList';
import { UT_TYPES } from '../constants';
import { useUtComparisonStore } from '../store/useUtComparisonStore';
import { AnalysisRow } from '../types';
import { calcTotals } from '../utils/analyze';
import { fmt } from '../utils/excelUtils';
import AnalysisOpinionSection, { OpinionSaveHandle } from './AnalysisOpinionSection';
import SectionBox from './SectionBox';

/**
 * 전체 종합의견 1건을 담을 때 사용하는 ANLS_OPIN_TP_CD.
 *
 * 공통코드(ANLS_OPIN_TP_CD)에 없는 임시 키다. AI 초안 저장 BE가 붙으면 항목별
 * 의견(A-1001/A-1002 …)으로 옮겨가고 이 상수는 사라진다.
 * — pages/ut/ref/BE-REQUEST-ai-report-opinion-v2.md 참고
 */
const OPIN_TP_CD_ALL = 'ALL';

/**
 * ⑦에 남길 AI 분석 항목 — **종합의견(A-1002)만.**
 *
 * [26.09.30] 종합분석(A-1001) 칸을 화면에서 걷어냈다. 목록이 공통코드에서 오므로
 * onlyCodes로 그릴 항목을 고른다(AnalysisOpinionSection).
 *
 * ⚠ A-1001의 저장된 행은 그대로 보존된다 — 이 섹션과 ⑥이 저장 직전에 최신 상태를 다시 읽어
 *   자기 항목이 아닌 행은 되돌려 보낸다. 화면에서만 빠진 것이고 DB는 건드리지 않는다.
 */
const SHOWN_OPIN_TP_CDS = ['A-1002'];

/** 분석 결과로부터 종합 분석 문구를 생성한다 (규칙 기반 요약) */
const buildAnalysisSummary = (rows: AnalysisRow[] | null, versionLabels: string[]): string => {
  if (!rows || rows.length === 0) return '';
  const n = versionLabels.length || 2;
  const s1 = versionLabels[0] || 'V1';
  const s2 = versionLabels[n - 1] || 'V2';
  const total = rows.length;
  const diff = rows.filter((r) => r.machineHasDiff).length;
  const totals = calcTotals(rows, n);

  const changes = UT_TYPES.map((ut) => {
    const arr = totals[ut.key];
    const v1 = arr[0];
    const v2 = arr[arr.length - 1];
    const d = v2 - v1;
    return { ut, v1, v2, d, pct: v1 === 0 ? null : (d / v1) * 100 };
  })
    .filter((x) => x.d !== 0)
    .sort((a, b) => Math.abs(b.d) - Math.abs(a.d));

  const desc = (x: (typeof changes)[number]) =>
    `${x.ut.key} ${fmt(x.v1)} → ${fmt(x.v2)} (${x.d > 0 ? '+' : ''}${fmt(x.d)}${
      x.pct !== null ? `, ${x.pct > 0 ? '+' : ''}${x.pct.toFixed(1)}%` : ''
    })`;

  const lines: string[] = [];
  lines.push(
    `${s1} → ${s2} 비교 결과, 총 ${total}건 중 ${diff}건(${((diff / total) * 100).toFixed(
      1
    )}%)에서 UT 사용량 변동이 확인되었습니다.`
  );

  if (changes.length === 0) {
    lines.push('UT 항목별 총 사용량 변동은 없습니다.');
  } else {
    const inc = changes.filter((x) => x.d > 0);
    const dec = changes.filter((x) => x.d < 0);
    if (inc.length > 0) lines.push(`· 증가 — ${inc.slice(0, 3).map(desc).join(' / ')}`);
    if (dec.length > 0) lines.push(`· 감소 — ${dec.slice(0, 3).map(desc).join(' / ')}`);
    lines.push(
      `변동폭 상위 항목(${changes
        .slice(0, 3)
        .map((x) => x.ut.key)
        .join(', ')})에 대해 설비 Q'ty 변경 이력과 단위 사용량 변동 원인 확인이 필요합니다.`
    );
  }
  return lines.join('\n');
};

interface Props {
  /**
   * ⑥ UT 항목별 분석결과의 저장 핸들 (UtComparisonPage가 물려준다).
   *
   * 화면의 저장 버튼은 이 섹션 하단 하나뿐이라, ⑦ 밖에 있는 ⑥도 여기서 함께 저장한다.
   */
  mechUtRef?: React.RefObject<OpinionSaveHandle>;
}

/**
 * ⑦ 종합의견 (TB_EELMB_UTM_RPT_OPIN_M)
 *
 * 저장된 리포트를 열었을 때(historyRptId)만 노출된다. 두 칸으로 구성된다.
 *
 *   1) 종합의견(AI) — 공통코드 A-1002, `AI_ANS_CTN`. 리포트 생성 시 AI가 채우고 사용자가 다듬는다.
 *   2) 종합의견    — `ANLS_OPIN_TP_CD='ALL'` 행, `OPIN_CTN`. 사용자가 직접 쓰는 칸.
 *
 * ⚠ 이름이 비슷하지만 **저장되는 행과 컬럼이 서로 다르다.** 한쪽에 값이 있어도 다른 쪽에는
 *   나오지 않는다.
 *
 * [26.09.30] 종합분석(A-1001) 칸만 화면에서 걷어내고 제목을 '종합의견'으로 줄였다.
 */
const OpinionSection = ({ mechUtRef }: Props) => {
  const historyRptId = useUtComparisonStore((s) => s.historyRptId);
  const analysisResult = useUtComparisonStore((s) => s.analysisResult);
  const versionLabels = useUtComparisonStore((s) => s.versionLabels);
  const userSession = useSessionStore();

  const [opinCtn, setOpinCtn] = useState('');
  const [savedSummary, setSavedSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  /** 하단 저장 버튼 하나로 모으기 위해 하위 섹션의 save()를 직접 호출한다 */
  const analysisRef = useRef<OpinionSaveHandle>(null);

  /** 화면에서 만든 요약 — 저장된 요약이 없을 때만 사용한다 */
  const generatedSummary = useMemo(
    () => buildAnalysisSummary(analysisResult, versionLabels),
    [analysisResult, versionLabels]
  );
  const summary = savedSummary || generatedSummary;

  useEffect(() => {
    if (!historyRptId) return;
    let alive = true;
    setLoading(true);
    setError('');
    getRptOpinions(historyRptId)
      .then((saved) => {
        if (!alive) return;
        const hit = saved.find((o) => o.anlsOpinTpCd === OPIN_TP_CD_ALL) ?? saved[0];
        setOpinCtn(hit?.opinCtn ?? '');
        setSavedSummary(hit?.anlsRltCtn ?? '');
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [historyRptId]);

  if (!historyRptId) return null;

  /**
   * 종합의견(ALL 행)만 저장.
   *
   * saveRptOpinions는 전체를 덮어쓰는 방식이라, 저장 직전에 최신 상태를 다시 읽어
   * 이 영역 밖의 행을 그대로 돌려보낸다 — 같은 화면의 AI 분석 항목(A-1001/A-1002)과
   * UT 항목별 분석결과 9종(⑥)이 같은 테이블에 있어서, 이 행 하나만 보내면 전부 지워진다.
   */
  const saveOverallOpinion = async (userId: string) => {
    const latest = await getRptOpinions(historyRptId).catch(() => [] as RptOpinion[]);
    const isAllRow = (o: RptOpinion) => o.atcDivsCd === 'MECH' && o.anlsOpinTpCd === OPIN_TP_CD_ALL;
    const prev = latest.find(isAllRow);
    const keep = latest.filter((o) => !isAllRow(o));

    // 저장 시점의 분석 요약을 함께 남겨 리포트 내용이 나중에 흔들리지 않게 한다.
    // AI 본문(AI_ANS_CTN)은 이 화면이 쓰지 않으므로 기존 값을 그대로 보존한다.
    const allRow: RptOpinion = {
      rptId: historyRptId,
      atcDivsCd: 'MECH',
      anlsOpinTpCd: OPIN_TP_CD_ALL,
      sortOrd: prev?.sortOrd ?? keep.length + 1,
      anlsRltCtn: summary,
      aiAnsCtn: prev?.aiAnsCtn ?? '',
      opinCtn,
    };

    await saveRptOpinions(historyRptId, [...keep, allRow], userId);
    setSavedSummary(summary);
  };

  /**
   * 화면 전체 저장 — ⑥ UT 항목별 분석결과 / ⑦ 종합의견(AI) / 종합의견을 이 버튼 하나로
   * 저장한다. 기계 탭의 저장 버튼은 이것뿐이다.
   *
   * ⚠ 세 영역은 **순서대로** 저장해야 한다. 같은 테이블(OPIN_M)을 통째로 덮어쓰는
   *   saveRptOpinions를 쓰기 때문에, 각 단계가 저장 직전에 최신 상태를 다시 읽어 머지한다.
   *   동시에 보내면 나중 요청이 앞 요청의 결과를 못 읽고 덮어쓴다.
   */
  const saveAll = async () => {
    if (!userSession.userId) return;
    setSaving(true);
    try {
      await analysisRef.current?.save();
      await mechUtRef?.current?.save();
      await saveOverallOpinion(userSession.userId);
      setSavedMsg('저장됨');
      window.setTimeout(() => setSavedMsg(''), 2000);
    } catch (e) {
      window.alert('저장 오류: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SectionBox no="⑦" title="종합의견" note={historyRptId}>
        {loading && <p className="hint-text">불러오는 중…</p>}
        {error && <p className="error-text">종합의견 조회 오류: {error}</p>}
        {!loading && !error && (
          <>
            {/*
              항목 목록은 공통코드 ANLS_OPIN_TP_CD(OPT_VAL_CTN1='MECH')에서 가져오고,
              그중 **종합의견(A-1002)만** 그린다(onlyCodes) — [26.09.30] 종합분석(A-1001) 제외.

              항목별 "검토 의견" 칸은 두지 않는다(showOpinion=false) — 아래에 종합의견
              입력칸이 따로 있어서다. 대신 리포트 생성 시 AI가 채워 넣은 분석 본문
              (AI_ANS_CTN)을 사용자가 직접 다듬을 수 있게 열어둔다(editableAnalysis).
              전기 탭(ElecOpinionPanel)과 같은 구성이다.
            */}
            <AnalysisOpinionSection
              ref={analysisRef}
              rptId={historyRptId}
              atcDivsCd="MECH"
              /* 제목이 '종합의견(AI)'이라 본문 위 'AI 분석' 라벨은 중복이다 */
              analysisLabel=""
              editableAnalysis
              showOpinion={false}
              hideSaveButton
              onlyCodes={SHOWN_OPIN_TP_CDS}
              /*
                공통코드명('권고 조치 사항') 대신 화면에서만 쓰는 제목.
                저장되는 항목코드는 그대로다 — 공통코드명을 바꾸면 이 덮어쓰기는 지우면 된다.
              */
              itemTitles={{ 'A-1002': '종합의견(AI)' }}
            />

            {/*
              UT 항목별 분석결과(ANLS_OPIN_TP_CD = CDA/N2/IW/… 9종)는 여기 있던 것을
              ⑥ 자리로 올렸다(MechUtAnalysisSection). 화면 밖으로 나갔을 뿐 저장은 여전히
              상자 밖 저장 버튼 하나가 담당한다 — saveAll이 mechUtRef로 그쪽 save()를 부른다.
            */}
            <p className="opin-label">종합의견</p>
            <textarea
              className="opin-textarea"
              value={opinCtn}
              placeholder="종합의견을 입력하세요"
              onChange={(e) => setOpinCtn(e.target.value)}
            />
          </>
        )}
      </SectionBox>
      {/*
        저장 버튼은 ⑦ 상자 밖에 둔다 — 이 버튼 하나가 ⑥·⑦ 전체를 저장하므로
        ⑦을 접어도 계속 보여야 한다. 화면 단위 버튼이라 규격도 GlobalBtnGroup을 쓴다.
      */}
      {!loading && !error && (
        <GlobalBtnGroup>
          {savedMsg && <span className="saved-msg">{savedMsg}</span>}
          <Button css={IconButton.button} className="save" onClick={saveAll} disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default OpinionSection;
